import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { AlertTriangle, ShieldCheck, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import MiniEligibilitySimulator from './MiniEligibilitySimulator';
import { CoefRow, ScoreTierRow } from './BaticoConfigRows';

export default function BaticoConfigTabs({ config, setConfig, updateList, addItem, removeItem, confirmDelete, newTerritoire, setNewTerritoire, handleAddTerritoire, variant }) {
  const handleAddRow = (listKey, defaults, noun, validate) => {
    const currentList = config?.[listKey] || [];
    if (typeof validate === 'function') {
      const err = validate(currentList, defaults);
      if (err) {
        toast.error(err);
        return;
      }
    }
    addItem(listKey, { ...defaults });
    toast.success(`${noun.charAt(0).toUpperCase() + noun.slice(1)} ajouté — éditez puis sauvegardez`);
  };

  const AddRowButton = ({ listKey, defaults, label, noun = 'ligne', testid, validate }) => (
    <Button
      type="button"
      onClick={() => handleAddRow(listKey, defaults, noun, validate)}
      variant="outline"
      size="sm"
      className="mt-2 h-8 border-dashed border-indigo-300 text-indigo-700 hover:bg-indigo-50"
      data-testid={testid}
    >
      <Plus className="h-3.5 w-3.5 mr-1" />
      {label}
    </Button>
  );

  // Validation : éviter d'ajouter une nouvelle qualif si la clé existe déjà.
  const validateUniqueQualifKey = (list, defaults) => {
    const key = (defaults?.key || '').trim().toLowerCase();
    if (!key) return null;
    let candidate = key;
    let i = 1;
    while (list.some((q) => (q.key || '').toLowerCase() === candidate)) {
      candidate = `${key}_${i++}`;
    }
    // Mute le defaults pour utiliser un suffixe disponible (-1, -2…).
    defaults.key = candidate;
    return null;
  };

  return (
    <>
              <MiniEligibilitySimulator config={config} variant={variant} />
        <Tabs defaultValue="experience">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="experience">Exp. & Ancienneté</TabsTrigger>
            <TabsTrigger value="effectif">Effectif</TabsTrigger>
            <TabsTrigger value="qualifs">Qualifications</TabsTrigger>
            <TabsTrigger value="territoires" data-testid="modulateurs-tab-territoires">
              Territoires <span className="ml-1 text-[10px] text-slate-400 hidden sm:inline">(Annuel)</span>
            </TabsTrigger>
            <TabsTrigger value="marche">Marché</TabsTrigger>
            <TabsTrigger value="ventilations">Ventilations</TabsTrigger>
            <TabsTrigger value="regles">Règles</TabsTrigger>
          </TabsList>

          <TabsContent value="experience" className="space-y-6 pt-4">
            <div>
              <h4 className="text-sm font-semibold mb-2 text-slate-700">Paliers expérience professionnelle</h4>
              {config.experience_tiers.map((item, idx) => (
                <CoefRow key={idx} item={item}
                  onChange={(v) => updateList('experience_tiers', idx, v)}
                  testidPrefix={`exp-${idx}`}
                  editableLabel
                  rangeFields={['min_years', 'max_years']}
                  onDelete={() => confirmDelete('experience_tiers', idx, item)}
                />
              ))}
              <AddRowButton
                listKey="experience_tiers"
                defaults={{ label: 'Nouveau palier', min_years: 0, max_years: null, coef: 1.0 }}
                label="Ajouter un palier d'expérience"
                noun="palier"
                testid="add-experience-tier"
              />
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-2 text-slate-700">Paliers ancienneté entreprise</h4>
              {config.anciennete_tiers.map((item, idx) => (
                <CoefRow key={idx} item={item}
                  onChange={(v) => updateList('anciennete_tiers', idx, v)}
                  testidPrefix={`anc-${idx}`}
                  editableLabel
                  rangeFields={['min_years', 'max_years']}
                  onDelete={() => confirmDelete('anciennete_tiers', idx, item)}
                />
              ))}
              <AddRowButton
                listKey="anciennete_tiers"
                defaults={{ label: 'Nouveau palier', min_years: 0, max_years: null, coef: 1.0 }}
                label="Ajouter un palier d'ancienneté"
                noun="palier"
                testid="add-anciennete-tier"
              />
            </div>

            {/* Score ancienneté (Couche 3) — points additifs sur le score d'éligibilité */}
            <div className="bg-amber-50/40 border border-amber-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold mb-1 text-amber-900 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-amber-600" /> Score ancienneté
              </h4>
              <p className="text-[11px] text-slate-600 mb-2">
                Points ajoutés au <strong>score d'éligibilité</strong> selon l'ancienneté de l'entreprise.
                Un score élevé déclenche la validation manuelle ou le refus.
              </p>
              {(config.score_anciennete_tiers || []).map((item, idx) => (
                <ScoreTierRow key={idx} item={item}
                  onChange={(v) => updateList('score_anciennete_tiers', idx, v)}
                  testidPrefix={`score-anc-${idx}`}
                  editable
                  onDelete={() => confirmDelete('score_anciennete_tiers', idx, item)}
                />
              ))}
              <AddRowButton
                listKey="score_anciennete_tiers"
                defaults={{ label: 'Nouveau palier', min_value: 0, max_value: null, points: 0 }}
                label="Ajouter un palier de score ancienneté"
                noun="palier"
                testid="add-score-anc-tier"
              />
            </div>

            {/* Score expérience dirigeant (Couche 3) */}
            <div className="bg-blue-50/40 border border-blue-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold mb-1 text-blue-900 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-blue-600" /> Score expérience dirigeant
              </h4>
              <p className="text-[11px] text-slate-600 mb-2">
                Points additifs selon l'expérience professionnelle du dirigeant.
              </p>
              {(config.score_experience_dirigeant_tiers || []).map((item, idx) => (
                <ScoreTierRow key={idx} item={item}
                  onChange={(v) => updateList('score_experience_dirigeant_tiers', idx, v)}
                  testidPrefix={`score-exp-${idx}`}
                  editable
                  onDelete={() => confirmDelete('score_experience_dirigeant_tiers', idx, item)}
                />
              ))}
              <AddRowButton
                listKey="score_experience_dirigeant_tiers"
                defaults={{ label: 'Nouveau palier', min_value: 0, max_value: null, points: 0 }}
                label="Ajouter un palier de score expérience dirigeant"
                noun="palier"
                testid="add-score-exp-tier"
              />
            </div>
          </TabsContent>

          <TabsContent value="effectif" className="pt-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold mb-2 text-slate-700">Paliers effectif (coef multiplicatif)</h4>
              <p className="text-[11px] text-amber-700 mb-2 italic">
                ⚠️ Le moteur fait correspondre les paliers d'effectif par <strong>libellé exact</strong> avec
                les options du formulaire — renommez prudemment.
              </p>
              {config.effectif_tiers.map((item, idx) => (
                <CoefRow
                  key={idx}
                  item={item}
                  onChange={(v) => updateList('effectif_tiers', idx, v)}
                  testidPrefix={`eff-${idx}`}
                  editableLabel
                  onDelete={() => confirmDelete('effectif_tiers', idx, item)}
                />
              ))}
              <AddRowButton
                listKey="effectif_tiers"
                defaults={{ label: 'Nouveau palier', coef: 1.0 }}
                label="Ajouter un palier d'effectif"
                noun="palier"
                testid="add-effectif-tier"
              />
            </div>
            <div className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold mb-1 text-emerald-900 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> Score effectif
              </h4>
              <p className="text-[11px] text-slate-600 mb-2">
                Points additifs selon le nombre de salariés.
              </p>
              {(config.score_effectif_tiers || []).map((item, idx) => (
                <ScoreTierRow key={idx} item={item}
                  onChange={(v) => updateList('score_effectif_tiers', idx, v)}
                  testidPrefix={`score-eff-${idx}`}
                  editable
                  onDelete={() => confirmDelete('score_effectif_tiers', idx, item)}
                />
              ))}
              <AddRowButton
                listKey="score_effectif_tiers"
                defaults={{ label: 'Nouveau palier', min_value: 0, max_value: null, points: 0 }}
                label="Ajouter un palier de score effectif"
                noun="palier"
                testid="add-score-eff-tier"
              />
            </div>
          </TabsContent>

          <TabsContent value="qualifs" className="pt-4 space-y-4">
            <div>
              <h4 className="text-sm font-semibold mb-2 text-slate-700">Qualifications professionnelles (coef tarifaire)</h4>
              <p className="text-[11px] text-slate-500 mb-2">
                Coefficient appliqué à la cotisation pour chaque qualification justifiée (Qualibat, Qualifelec, RGE…).
                La <strong>clé technique</strong> est utilisée par le moteur d'éligibilité (sans espaces, en minuscule).
              </p>
              {config.qualifications.map((item, idx) => (
                <CoefRow
                  key={idx}
                  item={item}
                  onChange={(v) => updateList('qualifications', idx, v)}
                  testidPrefix={`qualif-${idx}`}
                  editableLabel
                  editableKey
                  onDelete={() => confirmDelete('qualifications', idx, item)}
                />
              ))}
              <AddRowButton
                listKey="qualifications"
                defaults={{ key: 'nouvelle_qualif', label: 'Nouvelle qualification', coef: 1.0, activites_coherentes: [] }}
                label="Ajouter une qualification"
                noun="qualification"
                testid="add-qualification"
                validate={validateUniqueQualifKey}
              />
            </div>

            {/* Score qualifications (cohérence activité ↔ qualification) */}
            <div className="bg-purple-50/40 border border-purple-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold mb-1 text-purple-900 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 text-purple-600" /> Score qualifications (cohérence)
              </h4>
              <p className="text-[11px] text-slate-600 mb-2">
                Points ajoutés au <strong>score d'éligibilité</strong> selon la cohérence entre les qualifications
                justifiées et les activités déclarées. Exemples : Qualifelec ↔ électricité, QualiPV ↔ photovoltaïque,
                Qualibois ↔ charpente/ossature bois, Qualibat ↔ gros œuvre/second œuvre, OPQIBI ↔ ARCHOS/BET.
              </p>
              {[
                { key: 'score_qualif_coherente', label: 'Qualification cohérente avec l\'activité' },
                { key: 'score_qualif_forte_directe', label: 'Qualification forte et directement liée' },
                { key: 'score_aucune_qualif_activite_sensible', label: 'Aucune qualification sur activité sensible' },
                { key: 'score_aucune_qualif_activite_renforcee', label: 'Aucune qualification sur activité renforcée' },
              ].map(({ key, label }) => {
                const pts = Number(config[key]) || 0;
                const ptsClass = pts > 0 ? 'text-white bg-amber-600 border-amber-700 shadow-sm'
                  : pts < 0 ? 'text-white bg-emerald-600 border-emerald-700 shadow-sm'
                  : 'text-white bg-slate-600 border-slate-700 shadow-sm';
                return (
                  <div key={key} className="grid grid-cols-12 gap-2 items-end py-1.5 border-b border-slate-100 last:border-b-0">
                    <div className="col-span-12 sm:col-span-7">
                      <Label className="text-xs text-slate-700">{label}</Label>
                    </div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-[10px] text-slate-400 uppercase">Points</Label>
                      <Input type="number" step="1" value={config[key] ?? 0}
                        onChange={(e) => setConfig({ ...config, [key]: parseInt(e.target.value, 10) || 0 })}
                        className="h-8 text-sm font-mono"
                        data-testid={`score-qualif-${key}`}
                      />
                    </div>
                    <div className="col-span-6 sm:col-span-3 text-right">
                      <Badge variant="outline" className={`font-mono text-xs ${ptsClass}`}>
                        {pts > 0 ? '+' : ''}{pts} pts
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="territoires" className="pt-4 space-y-3">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div className="text-[12px] text-emerald-900 leading-snug">
                <strong>Portée :</strong> Calculateurs <strong>RCDS Annuel</strong>, <strong>PIB Annuel</strong>
                &amp; <strong>Avenant Annuel</strong>.
                Ces coefficients de territoires sont pondérés par le CA réparti (€) et servent au calcul du
                coefficient global de cotisation annuelle.
                <span className="block text-[11px] text-emerald-700 mt-0.5 italic">
                  Les souscriptions <strong>nominatives</strong> (RCDS Nominative, Avenant Nominatif,
                  PIB Nominatif) utilisent la liste séparée « Territoires d&apos;Intervention » de
                  l&apos;onglet <em>Opérations</em>.
                </span>
              </div>
            </div>
            {config.territoires.map((item, idx) => (
              <div key={item.key || idx} className="flex items-center gap-2" data-testid={`territoire-row-${idx}`}>
                <div className="flex-1">
                  <CoefRow
                    item={item}
                    onChange={(v) => updateList('territoires', idx, v)}
                    testidPrefix={`terr-${idx}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Supprimer le territoire « ${item.label} » ?\nPensez à sauvegarder.`)) {
                      removeItem('territoires', idx);
                    }
                  }}
                  className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded transition-colors flex-shrink-0"
                  title="Supprimer ce territoire"
                  aria-label={`Supprimer ${item.label}`}
                  data-testid={`territoire-delete-${idx}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Formulaire d'ajout — territoire personnalisé */}
            <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50/40 p-3 space-y-2"
                 data-testid="add-territoire-form">
              <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold flex items-center gap-1">
                <Plus className="h-3 w-3" />
                Ajouter un territoire
              </div>
              <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3">
                  <Label htmlFor="terr-new-key" className="text-[10px] text-slate-600">Clé technique</Label>
                  <Input
                    id="terr-new-key"
                    type="text"
                    placeholder="ex: saint_martin"
                    value={newTerritoire.key}
                    onChange={(e) => setNewTerritoire(s => ({ ...s, key: e.target.value }))}
                    className="h-8 text-sm"
                    data-testid="new-territoire-key"
                  />
                </div>
                <div className="col-span-5">
                  <Label htmlFor="terr-new-label" className="text-[10px] text-slate-600">Libellé</Label>
                  <Input
                    id="terr-new-label"
                    type="text"
                    placeholder="ex: Saint-Martin"
                    value={newTerritoire.label}
                    onChange={(e) => setNewTerritoire(s => ({ ...s, label: e.target.value }))}
                    className="h-8 text-sm"
                    data-testid="new-territoire-label"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="terr-new-coef" className="text-[10px] text-slate-600">Coef</Label>
                  <Input
                    id="terr-new-coef"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="1.00"
                    value={newTerritoire.coef}
                    onChange={(e) => setNewTerritoire(s => ({ ...s, coef: e.target.value }))}
                    className="h-8 text-sm"
                    data-testid="new-territoire-coef"
                  />
                </div>
                <div className="col-span-2">
                  <Button
                    type="button"
                    onClick={handleAddTerritoire}
                    size="sm"
                    className="w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="add-territoire-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Ajouter
                  </Button>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                Astuce : la <strong>clé technique</strong> doit être unique et sans espaces (ex: « saint_martin »).
                Le <strong>coef</strong> multiplie la cotisation pour le CA réparti sur ce territoire (1.0 = neutre,
                &lt; 1 = avantage, &gt; 1 = surcharge).
              </p>
            </div>
          </TabsContent>

          <TabsContent value="marche" className="pt-4 space-y-1">
            {config.types_marche.map((item, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end py-1.5 border-b border-slate-100 last:border-b-0">
                <div className="col-span-12 sm:col-span-4">
                  <Label className="text-xs text-slate-600">{item.label}</Label>
                  {item.blocking && <Badge variant="destructive" className="ml-2 text-[10px]"><AlertTriangle className="h-2.5 w-2.5 mr-1" />Bloquant</Badge>}
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] text-slate-400 uppercase">Coef</Label>
                  <Input type="number" step="0.01" min="0" value={item.coef}
                    onChange={(e) => updateList('types_marche', idx, { ...item, coef: Number(e.target.value) || 0 })}
                    className="h-8 text-sm font-mono" />
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Label className="text-[10px] text-slate-400 uppercase">Score</Label>
                  <Input type="number" min="0" value={item.score_bonus}
                    onChange={(e) => updateList('types_marche', idx, { ...item, score_bonus: Number(e.target.value) || 0 })}
                    className="h-8 text-sm font-mono" />
                </div>
                <div className="col-span-4 sm:col-span-2 flex items-center gap-1.5">
                  <input type="checkbox" checked={item.blocking || false}
                    onChange={(e) => updateList('types_marche', idx, { ...item, blocking: e.target.checked })}
                    className="h-3.5 w-3.5" />
                  <span className="text-[11px] text-slate-500">Bloquant</span>
                </div>
                <div className="col-span-12 sm:col-span-2 flex items-center gap-1.5">
                  <input type="checkbox" checked={item.validation_manuelle || false}
                    onChange={(e) => updateList('types_marche', idx, { ...item, validation_manuelle: e.target.checked })}
                    className="h-3.5 w-3.5" />
                  <span className="text-[11px] text-slate-500">Validation manuelle</span>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="ventilations" className="pt-4 space-y-5">
            {[
              { key: 'ventilation_clientele', title: 'Ventilation par secteur de clientèle' },
              { key: 'ventilation_contrat', title: 'Ventilation par nature de contrat' },
              { key: 'ventilation_travaux', title: 'Ventilation par nature de travaux' },
            ].map((section) => (
              <div key={section.key}>
                <h4 className="text-sm font-semibold mb-2 text-slate-700">{section.title}</h4>
                {(config[section.key] || []).map((item, idx) => (
                  <CoefRow key={idx} item={item} onChange={(v) => updateList(section.key, idx, v)}
                    fields={['pct_modulateur']} testidPrefix={`${section.key}-${idx}`}
                    extra={<span className="text-[11px] text-slate-400">{item.pct_modulateur >= 0 ? '+' : ''}{item.pct_modulateur}% sur cette part</span>}
                  />
                ))}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="regles" className="pt-4 space-y-4">
            <div>
              <Label className="text-xs">Nombre maximum d&apos;activités BATICO</Label>
              <Input type="number" min="1" max="50" value={config.max_activites_batico}
                onChange={(e) => setConfig({ ...config, max_activites_batico: Number(e.target.value) || 15 })}
                className="h-9 w-32 font-mono" data-testid="max-activites-batico" />
              <p className="text-[11px] text-slate-500 mt-1">Au-delà : REFUS automatique.</p>
            </div>
            <div>
              <Label className="text-xs">Score bonus si &gt; max activités</Label>
              <Input type="number" min="0" value={config.score_bonus_batico_plus_15}
                onChange={(e) => setConfig({ ...config, score_bonus_batico_plus_15: Number(e.target.value) || 35 })}
                className="h-9 w-32 font-mono" />
            </div>
            <div>
              <Label className="text-xs">Taux de majoration par défaut (×)</Label>
              <Input type="number" step="0.05" min="1" max="5"
                value={config.taux_majoration_personnalisee_default ?? 1.30}
                onChange={(e) => setConfig({ ...config, taux_majoration_personnalisee_default: Number(e.target.value) || 1.30 })}
                className="h-9 w-32 font-mono" data-testid="taux-majoration-default" />
              <p className="text-[11px] text-slate-500 mt-1">
                Coefficient multiplicateur appliqué automatiquement quand le dossier
                dépasse la dernière classe (ex&nbsp;: ×1.30 = +30%). Le Super Admin peut
                surcharger ce taux directement dans le simulateur.
              </p>
            </div>
          </TabsContent>
        </Tabs>
    </>
  );
}
